import { useState, useEffect } from "react";
import { safeHref } from "../lib/safeUrl";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import Layout from "../components/Layout";
import EditableText from "../components/EditableText";
import TaxonomyManager from "../components/TaxonomyManager";
import AdminManageMenu from "../components/AdminManageMenu";
import AdminEditResourceModal from "../components/AdminEditResourceModal";
import { 
  Search, Plus, BookOpen, FileText, Video, Link as LinkIcon, 
  ExternalLink, Tag, Pencil
} from "lucide-react";

const ResourcesPage = () => {
  const { user } = useAuth();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const [showNewResource, setShowNewResource] = useState(false);
  const [newResource, setNewResource] = useState({
    title: "",
    description: "",
    resource_type: "article",
    url: "",
    tags: []
  });
  const [creating, setCreating] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const RESOURCE_TYPES = [
    { value: "article", label: "Article", icon: FileText },
    { value: "video", label: "Video", icon: Video },
    { value: "document", label: "Document", icon: BookOpen },
    { value: "link", label: "Link", icon: LinkIcon }
  ];

  const COMMON_TAGS = [
    "Clinical", "Education", "Business", "Technology", "Wellness",
    "Research", "Patient Care", "Infection Control", "Equipment"
  ];

  useEffect(() => {
    fetchResources();
  }, [search, selectedType, selectedTag]);

  const fetchResources = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (selectedType) params.append("resource_type", selectedType);
      if (selectedTag) params.append("tag", selectedTag);

      const response = await axios.get(`${API}/resources?${params.toString()}`);
      setResources(response.data);
    } catch (error) {
      toast.error("Failed to load resources");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateResource = async (e) => {
    e.preventDefault();
    if (!newResource.title.trim() || !newResource.description.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API}/resources`, newResource);
      toast.success("Resource added!");
      setShowNewResource(false);
      setNewResource({ title: "", description: "", resource_type: "article", url: "", tags: [] });
      fetchResources();
    } catch (error) {
      toast.error("Failed to add resource");
    } finally {
      setCreating(false);
    }
  };

  const toggleTag = (tag) => {
    setNewResource(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const getTypeIcon = (type) => {
    const found = RESOURCE_TYPES.find(t => t.value === type);
    return found ? found.icon : FileText;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-CA", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="resources-page">
        {/* Disclaimer */}
        <div className="bg-[#FAF7F2] border border-[#E5DFEC] rounded-xl p-3 mb-6 text-xs text-[#64748B]">
          Resources are for informational purposes only. Links to external websites are provided for convenience. DHDH does not own or control third-party content. Always verify information with official regulatory bodies.
        </div>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-medium text-[#6B4E8A] inline-flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <EditableText blockKey="resources.title" fallback="Resources" as="span" />
              <TaxonomyManager
                kind="resource_categories"
                title="Resource categories"
                description="Manage the resource-category tags."
                triggerLabel="Manage resource categories"
              />
            </h1>
            <EditableText
              blockKey="resources.subtitle"
              fallback="Educational materials and professional resources"
              as="p"
              className="text-[#64748B] mt-1"
              multiline
            />
          </div>
          <Dialog open={showNewResource} onOpenChange={setShowNewResource}>
            <DialogTrigger asChild>
              <Button className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full" data-testid="add-resource-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Resource
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-xl font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Add a Resource
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateResource} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={newResource.title}
                    onChange={(e) => setNewResource({ ...newResource, title: e.target.value })}
                    placeholder="Resource title"
                    className="mt-2 bg-white border-[#E5DFEC] focus:border-[#6B4E8A]"
                    data-testid="resource-title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="type">Type</Label>
                  <Select
                    value={newResource.resource_type}
                    onValueChange={(value) => setNewResource({ ...newResource, resource_type: value })}
                  >
                    <SelectTrigger className="mt-2 bg-white border-[#E5DFEC]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RESOURCE_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon className="w-4 h-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={newResource.description}
                    onChange={(e) => setNewResource({ ...newResource, description: e.target.value })}
                    placeholder="Brief description of this resource..."
                    className="mt-2 bg-white border-[#E5DFEC] focus:border-[#6B4E8A]"
                    data-testid="resource-description-input"
                  />
                </div>
                <div>
                  <Label htmlFor="url">URL (Optional)</Label>
                  <Input
                    id="url"
                    type="url"
                    value={newResource.url}
                    onChange={(e) => setNewResource({ ...newResource, url: e.target.value })}
                    placeholder="https://..."
                    className="mt-2 bg-white border-[#E5DFEC] focus:border-[#6B4E8A]"
                    data-testid="resource-url-input"
                  />
                </div>
                <div>
                  <Label>Tags</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {COMMON_TAGS.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => toggleTag(tag)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          newResource.tags.includes(tag)
                            ? 'bg-[#6B4E8A] text-white'
                            : 'bg-[#F0ECF7] text-[#6B4E8A] hover:bg-[#E5DFEC]'
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowNewResource(false)}
                    className="rounded-full"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full"
                    disabled={creating}
                    data-testid="submit-resource-btn"
                  >
                    {creating ? "Adding..." : "Add Resource"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="bg-white border border-[#E5DFEC] rounded-2xl p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search resources..."
                className="pl-12 bg-[#FAF7F2] border-[#E5DFEC] focus:border-[#6B4E8A]"
                data-testid="search-resources-input"
              />
            </div>
            <Select value={selectedType || "all"} onValueChange={(v) => setSelectedType(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[160px] bg-[#FAF7F2] border-[#E5DFEC]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {RESOURCE_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedTag || "all"} onValueChange={(v) => setSelectedTag(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[160px] bg-[#FAF7F2] border-[#E5DFEC]">
                <Tag className="w-4 h-4 mr-2 text-[#64748B]" />
                <SelectValue placeholder="All Tags" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tags</SelectItem>
                {COMMON_TAGS.map((tag) => (
                  <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Resources Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white border border-[#E5DFEC] rounded-2xl p-6 animate-pulse">
                <div className="h-10 w-10 bg-[#F0ECF7] rounded-xl mb-4"></div>
                <div className="h-6 bg-[#F0ECF7] rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-[#F0ECF7] rounded w-full mb-2"></div>
                <div className="h-4 bg-[#F0ECF7] rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : resources.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {resources.map((resource, idx) => {
              const TypeIcon = getTypeIcon(resource.resource_type);
              const isExpanded = expandedId === resource.resource_id;
              return (
                <motion.div
                  key={resource.resource_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className={`relative bg-white border border-[#E5DFEC] rounded-2xl p-6 card-hover ${isExpanded ? "lg:col-span-3 md:col-span-2" : ""}`}
                  data-testid={`resource-card-${resource.resource_id}`}
                >
                  <div className="absolute top-3 right-3 z-10">
                    <AdminManageMenu
                      resourceType="resource"
                      resourceId={resource.resource_id}
                      resourceLabel={resource.title}
                      onDeleted={fetchResources}
                      extraActions={[
                        {
                          label: "Edit resource",
                          icon: Pencil,
                          onClick: () => setEditingResource(resource),
                        },
                      ]}
                    />
                  </div>
                  <div className="flex items-start justify-between mb-4 pr-8">
                    <div className="w-12 h-12 rounded-xl bg-[#F0ECF7] flex items-center justify-center">
                      <TypeIcon className="w-6 h-6 text-[#8DB5A0]" />
                    </div>
                    {resource.url && (
                      <a
                        href={safeHref(resource.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="p-2 hover:bg-[#F0ECF7] rounded-lg transition-colors"
                      >
                        <ExternalLink className="w-4 h-4 text-[#64748B]" />
                      </a>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setExpandedId(isExpanded ? null : resource.resource_id)}
                    className="block w-full text-left"
                    data-testid={`resource-toggle-${resource.resource_id}`}
                  >
                    <h3 className="text-lg font-medium text-[#6B4E8A] mb-2 hover:text-[#553D6F]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {resource.title}
                    </h3>
                  </button>
                  {isExpanded ? (
                    <div data-testid={`resource-expanded-${resource.resource_id}`}>
                      {resource.image_url && (
                        <img src={resource.image_url} alt="" className="w-full max-h-72 object-cover rounded-xl mb-3" />
                      )}
                      <p className="text-sm text-[#475569] whitespace-pre-wrap mb-4 leading-relaxed">
                        {resource.description || resource.content || "(no body)"}
                      </p>
                      {resource.url && (
                        <a
                          href={safeHref(resource.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-[#6B4E8A] hover:text-[#553D6F] mb-3"
                        >
                          <ExternalLink className="w-3.5 h-3.5" /> Visit external link
                        </a>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-[#64748B] line-clamp-3 mb-4">
                      {resource.description}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {resource.tags?.slice(0, 3).map((tag) => (
                      <span key={tag} className="px-2 py-0.5 bg-[#F0ECF7] text-[#6B4E8A] text-xs rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between text-xs text-[#64748B]">
                    <span>Added {formatDate(resource.created_at)}</span>
                    <button
                      type="button"
                      onClick={() => setExpandedId(isExpanded ? null : resource.resource_id)}
                      className="text-[#6B4E8A] hover:text-[#553D6F] font-medium"
                      data-testid={`resource-expand-cta-${resource.resource_id}`}
                    >
                      {isExpanded ? "Show less ↑" : "Read more →"}
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16 bg-white border border-[#E5DFEC] rounded-2xl">
            <BookOpen className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
            <h3 className="text-lg font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              No resources found
            </h3>
            <p className="text-[#64748B] mb-4">
              {search || selectedType || selectedTag
                ? "Try adjusting your filters"
                : "Be the first to share a resource!"}
            </p>
            <Button
              onClick={() => setShowNewResource(true)}
              className="bg-[#8DB5A0] text-white hover:bg-[#D0694E] rounded-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Resource
            </Button>
          </div>
        )}
      </div>
      <AdminEditResourceModal
        resource={editingResource}
        resourceType="resource"
        open={!!editingResource}
        onClose={() => setEditingResource(null)}
        onSaved={() => fetchResources()}
      />
    </Layout>
  );
};

export default ResourcesPage;
